import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import {
  LayoutDashboard, Sparkles, CalendarDays, FileText, UserCog,
  Settings2, LogOut, Palette, Radar, Tag, Gift, Wand2, Layers3, Menu, X,
} from "lucide-react";

const navItems = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/creer", label: "Créer un post", icon: Sparkles, testid: "nav-creer" },
  { to: "/studio", label: "Studio créatif", icon: Wand2, testid: "nav-studio" },
  { to: "/veille", label: "Veille & idées", icon: Radar, testid: "nav-veille" },
  { to: "/calendrier", label: "Calendrier éditorial", icon: CalendarDays, testid: "nav-calendrier" },
  { to: "/posts", label: "Mes posts", icon: FileText, testid: "nav-posts" },
  { to: "/marque", label: "Ma marque", icon: Palette, testid: "nav-marque" },
  { to: "/tarifs", label: "Offres & parrainage", icon: Tag, testid: "nav-tarifs", billingOnly: true },
  { to: "/parrainage", label: "Mon parrainage", icon: Gift, testid: "nav-parrainage", billingOnly: true },
  { to: "/onboarding", label: "Profil de style", icon: Settings2, testid: "nav-onboarding" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unseen, setUnseen] = useState(0);
  const [atelier, setAtelier] = useState({ ready: 0, working: 0 });
  const [billingMode, setBillingMode] = useState("off");

  useEffect(() => {
    api.get("/watch/unseen").then((r) => setUnseen(r.data.unseen || 0)).catch(() => {});
    api.get("/billing/config").then((r) => setBillingMode(r.data.billing_mode || "off")).catch(() => {});
  }, []);

  // Pastille Atelier : propositions prêtes non consultées, rafraîchie toutes les 30 s.
  useEffect(() => {
    const refresh = () => api.get("/atelier/summary")
      .then((r) => setAtelier({ ready: r.data.ready || 0, working: r.data.working || 0 }))
      .catch(() => {});
    refresh();
    const id = setInterval(refresh, 30000);
    window.addEventListener("atelier-refresh", refresh);
    return () => { clearInterval(id); window.removeEventListener("atelier-refresh", refresh); };
  }, []);

  const visibleNav = user?.plan === "affiliate_only"
    ? navItems.filter((i) => i.to === "/parrainage")
    : navItems.filter((i) => !i.billingOnly || billingMode !== "off");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const ent = user?.entitlement || {};
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "long", year: "numeric" }) : "");
  let banner = null;
  if (ent.source === "invite_meeting" && ent.pending) {
    banner = { kind: "info", testid: "banner-pending", text: `Votre accès invité s'ouvrira le ${fmtDate(ent.access_starts_at)}. À très vite !`, cta: null };
  } else if (ent.source === "member_grace") {
    banner = { kind: "amber", testid: "banner-grace", text: `Votre accès Membre APOLLO se termine le ${fmtDate(ent.grace_ends_at)}. Renouvelez votre adhésion ou passez à Solo pour ne pas l'interrompre.`, cta: { to: "/tarifs", label: "Voir mes options" } };
  } else if (ent.active === false && (ent.expired || ent.source === "member_expired")) {
    banner = { kind: "amber", testid: "banner-readonly", text: "Votre accès est en lecture seule. Souscrivez pour continuer à créer et publier.", cta: { to: "/tarifs", label: "Souscrire" } };
  }
  if (!banner && user?.email_verified === false) {
    banner = { kind: "info", testid: "banner-confirm-email", text: "Confirmez votre adresse e-mail grâce au lien que nous vous avons envoyé. Membre APOLLO ? Votre accès Membre s'active dès la confirmation.", resend: true };
  }
  const resend = async () => {
    try { await api.post("/auth/resend-confirmation"); toast.success("Nouveau lien envoyé"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <div className="lg:grid lg:grid-cols-[280px_1fr] min-h-screen">
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 bg-white border-b border-border px-4 h-14" data-testid="mobile-topbar">
        <button onClick={() => setMenuOpen(true)} aria-label="Ouvrir le menu" data-testid="mobile-menu-btn"
          className="p-2 -ml-2 rounded-lg text-ink hover:bg-secondary"><Menu className="h-6 w-6" /></button>
        <div className="scale-90 origin-right"><BrandLogo /></div>
      </header>
      {menuOpen && <div className="lg:hidden fixed inset-0 z-40 bg-ink/40" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-overlay" />}
      <aside className={`bg-white border-r border-border text-ink flex flex-col h-screen fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] overflow-y-auto transition-transform duration-200 lg:sticky lg:top-0 lg:z-auto lg:w-auto lg:max-w-none lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="app-sidebar">
        <div className="px-7 py-8 border-b border-border flex items-center justify-between">
          <BrandLogo />
          <button onClick={() => setMenuOpen(false)} aria-label="Fermer le menu" data-testid="mobile-menu-close"
            className="lg:hidden p-2 -mr-3 rounded-lg text-ink-light hover:bg-secondary"><X className="h-5 w-5" /></button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? "bg-coral-soft text-coral-dark" : "text-ink-light hover:text-ink hover:bg-secondary"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              {item.label}
              {item.to === "/veille" && unseen > 0 && (
                <span data-testid="veille-badge" className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-coral-strong rounded-full">
                  {unseen}
                </span>
              )}
              {item.to === "/creer" && atelier.ready > 0 && (
                <span data-testid="atelier-badge" className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-coral-strong rounded-full">
                  {atelier.ready}
                </span>
              )}
              {item.to === "/creer" && atelier.ready === 0 && atelier.working > 0 && (
                <span data-testid="atelier-working" title={`${atelier.working} demande(s) en cours`}
                  className="ml-auto h-2 w-2 rounded-full bg-coral-strong animate-pulse" />
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? "bg-coral-soft text-coral-dark" : "text-ink-light hover:text-ink hover:bg-secondary"
                }`
              }
            >
              <UserCog className="h-[18px] w-[18px]" />
              Back-office admin
            </NavLink>
          )}
        </nav>

        <div className="px-4 py-5 border-t border-border">
          <div className="px-3 mb-3">
            <p className="text-sm font-bold truncate">{user?.name}</p>
            <p className="text-xs text-ink-light/70 truncate">{user?.email}</p>
          </div>
          <Link to="/confidentialite" data-testid="account-privacy-link" className="block px-3 mb-2 text-xs font-semibold text-ink-light hover:text-coral-dark transition-colors">Confidentialité</Link>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-ink-light hover:text-ink hover:bg-secondary transition-colors"
          >
            <LogOut className="h-[18px] w-[18px]" />
            Se déconnecter
          </button>
        </div>
      </aside>

      <main className="min-w-0 overflow-x-hidden lg:overflow-y-auto">
        {banner && (
          <div data-testid={banner.testid}
            className={`flex flex-wrap items-center justify-between gap-3 px-4 sm:px-8 py-3 text-sm font-medium border-b ${banner.kind === "amber" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-coral-soft/60 border-coral/20 text-coral-dark"}`}>
            <span>{banner.text}</span>
            {banner.cta && (
              <button onClick={() => navigate(banner.cta.to)} data-testid="banner-cta"
                className="shrink-0 bg-ink text-white px-3.5 py-1.5 rounded-sm text-xs font-semibold hover:bg-ink/90 transition-colors">{banner.cta.label}</button>
            )}
            {banner.resend && (
              <button onClick={resend} data-testid="banner-resend-confirmation"
                className="shrink-0 bg-ink text-white px-3.5 py-1.5 rounded-sm text-xs font-semibold hover:bg-ink/90 transition-colors">Renvoyer le lien</button>
            )}
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
